/**
 * The Initiative list's view state as a URL search string.
 *
 * The same contract the calendar codec holds: the URL is the single source of
 * truth, every control mutates it rather than keeping a copy, and an unparseable
 * or stale value falls back to the default instead of throwing, so a hand-edited
 * or bookmarked URL still opens (R11.15, R11.16). Filters, sort direction, page
 * size and page offset all live here, which is what makes a filtered page of the
 * list linkable.
 *
 * ## Why this is a separate codec from `calendar-search-params`
 *
 * The two screens carry different state. The calendar has an orientation, a year
 * in its route path and every filter dimension; the list has a page window, a page
 * size and the three dimensions this screen exposes. Sharing one codec would mean
 * one screen parsing keys it never renders, and a default for `orientation` on a
 * screen that has none.
 *
 * What the two screens *do* share is the filter grammar itself, and that lives in
 * `filter-search-params.ts`: the dimension classification, the value primitives and
 * the mutation helpers. This module is a per-screen wrapper over that codec, not a
 * second implementation of it.
 *
 * ## The planning year, and why no year control
 *
 * `GET /api/initiatives` requires `planningYear`, but it is not a row predicate
 * there: the endpoint documents it as the frame the month, calendar-week and
 * Sales_Cycle date filters are computed against, none of which this screen
 * exposes. The list is therefore *not* year-scoped - it returns every Initiative a
 * user may read, in any year - which is the behaviour this screen wants, so the
 * year is supplied as the current system year and never offered as a control. A
 * year control here would look like it narrowed the rows and would not.
 */
import { initiativeStatusSchema, INITIATIVE_STATUSES, MAX_PAGE_SIZE } from '@complan/contracts';
// The list reads two dimensions of the shared filter grammar, so it reads them with
// the shared primitives rather than its own copies (`complan-governance` R6.1). What
// stays local is the page window and the sort, which are this screen's alone.
import { parseFilterDimensions, readList, readSearchTerm } from './filter-search-params';

/**
 * One Initiative status. Derived from the contract's own tuple rather than
 * declared here, so the two cannot drift; the contracts package exports the
 * schema and the tuple but no ready-made type alias.
 */
export type InitiativeStatus = (typeof INITIATIVE_STATUSES)[number];

/** The page sizes the size control offers. */
export const LIST_PAGE_SIZES = [20, 50, 100] as const;

/** The page size a first visit uses. */
export const DEFAULT_LIST_PAGE_SIZE = 20;

/** The sort field this screen sorts by: the Initiative identifier. */
export const LIST_SORT_FIELD = 'initiative_ref' as const;

/** A page size the control offers. */
export type ListPageSize = (typeof LIST_PAGE_SIZES)[number];

export interface InitiativeListState {
  /** R11.4's status dimension; empty is the default scope, which excludes Cancelled. */
  readonly statuses: readonly InitiativeStatus[];
  /** R11.2's Platform dimension. */
  readonly platformIds: readonly string[];
  /** The free-text Search term, or `null` when the box is empty. */
  readonly search: string | null;
  readonly sortDirection: 'asc' | 'desc';
  readonly limit: number;
  readonly offset: number;
}

export const DEFAULT_LIST_STATE: InitiativeListState = {
  statuses: [],
  platformIds: [],
  search: null,
  sortDirection: 'desc',
  limit: DEFAULT_LIST_PAGE_SIZE,
  offset: 0,
};

/** Parse the list view state from a URL search string. */
export function parseInitiativeListSearch(search: URLSearchParams): InitiativeListState {
  return {
    // An unknown status label is dropped rather than rejected: a stale URL naming
    // a status that no longer exists still opens, with that one value ignored
    // (R11.16's "apply the remaining values").
    statuses: readList(search, 'statuses').filter(isInitiativeStatus),
    platformIds: readList(search, 'platformIds'),
    search: readSearchTerm(search),
    sortDirection: search.get('sortDirection') === 'asc' ? 'asc' : 'desc',
    limit: readPageSize(search),
    offset: readOffset(search),
  };
}

/**
 * Serialise the state back to a search string, omitting everything left at its
 * default so a first visit has an empty query string and the URL stays readable.
 */
export function toInitiativeListSearch(state: InitiativeListState): URLSearchParams {
  const params = new URLSearchParams();
  for (const status of state.statuses) {
    params.append('statuses', status);
  }
  for (const platformId of state.platformIds) {
    params.append('platformIds', platformId);
  }
  if (state.search !== null) {
    params.set('search', state.search);
  }
  if (state.sortDirection !== 'desc') {
    params.set('sortDirection', state.sortDirection);
  }
  if (state.limit !== DEFAULT_LIST_PAGE_SIZE) {
    params.set('limit', String(state.limit));
  }
  if (state.offset !== 0) {
    params.set('offset', String(state.offset));
  }
  return params;
}

/**
 * The params this screen owns and therefore rewrites: the three dimensions it exposes
 * as controls, plus its sort and page window.
 *
 * Everything *not* in this list is carried through untouched by
 * {@link applyListStateToSearch}. See that function for why that matters.
 */
const OWNED_PARAMS = [
  'statuses',
  'platformIds',
  'search',
  'sortDirection',
  'limit',
  'offset',
] as const;

/**
 * Apply the list's state to the URL it is already showing, preserving every param the
 * screen does not own.
 *
 * **This is what the page must call, not {@link toInitiativeListSearch}.** The
 * difference is the Filter_Context (`complan-governance` R6.3, R6.9). A
 * Drill_Through from a dashboard arrives here carrying the whole selection - Category,
 * month, watchout severity, the planning year - of which this screen renders controls
 * for three dimensions. Rebuilding the search string from `InitiativeListState` alone
 * would therefore discard every other dimension the moment the user touched a filter,
 * and the list's count would quietly stop matching the metric it was drilled from,
 * which is precisely what R6.9 forbids.
 *
 * Carrying unknown params through also keeps the planning year alive for the *next*
 * screen. The list does not filter by year (see this module's header) but must not be
 * the link in the chain that drops it.
 */
export function applyListStateToSearch(
  current: URLSearchParams,
  state: InitiativeListState,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of OWNED_PARAMS) {
    next.delete(key);
  }

  for (const status of state.statuses) {
    next.append('statuses', status);
  }
  for (const platformId of state.platformIds) {
    next.append('platformIds', platformId);
  }
  if (state.search !== null) {
    next.set('search', state.search);
  }
  // Defaults stay absent, so a default view's URL keeps whatever it arrived with and
  // adds nothing of its own.
  if (state.sortDirection !== 'desc') {
    next.set('sortDirection', state.sortDirection);
  }
  if (state.limit !== DEFAULT_LIST_PAGE_SIZE) {
    next.set('limit', String(state.limit));
  }
  if (state.offset !== 0) {
    next.set('offset', String(state.offset));
  }
  return next;
}

/**
 * The query string for `GET /api/initiatives`.
 *
 * Distinct from {@link toInitiativeListSearch} on purpose: the browser URL omits
 * defaults, whereas the request must state `planningYear` (the endpoint requires
 * it) and the sort field (the endpoint's own default is `start_date`, and this
 * screen sorts by identifier). Only the dimensions this screen exposes are sent;
 * every other dimension is left absent, which the filter grammar reads as
 * unfiltered.
 */
export function toInitiativeListRequestQuery(
  state: InitiativeListState,
  planningYear: number,
  /**
   * The screen's current search params, so the request also states the dimensions
   * this screen carries but renders no control for.
   *
   * Optional, and absent means "this screen's three dimensions only" - which is what
   * a first visit is. When present, it is what keeps R6.9 true: the URL, the request
   * and therefore the reported count all describe one selection. Without it a
   * Drill_Through would put `categoryIds` in the URL, never send it, and report a
   * count larger than the metric the user clicked.
   */
  carried?: URLSearchParams,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set('planningYear', String(carriedPlanningYear(carried) ?? planningYear));
  for (const status of state.statuses) {
    params.append('statuses', status);
  }
  for (const platformId of state.platformIds) {
    params.append('platformIds', platformId);
  }
  if (state.search !== null) {
    params.set('search', state.search);
  }
  params.set('sortField', LIST_SORT_FIELD);
  params.set('sortDirection', state.sortDirection);
  params.set('limit', String(state.limit));
  params.set('offset', String(state.offset));

  // Every recognised dimension the URL carries that this screen does not own. Read
  // through the shared codec, so a dimension is carried because the filter grammar
  // defines it, not because this module remembered to list it.
  if (carried !== undefined) {
    for (const [key, value] of Object.entries(parseFilterDimensions(carried))) {
      if ((OWNED_PARAMS as readonly string[]).includes(key)) {
        continue;
      }
      if (Array.isArray(value)) {
        for (const entry of value) {
          params.append(key, String(entry));
        }
      } else {
        params.set(key, String(value));
      }
    }
  }
  return params;
}

/** The planning year the URL carries, if it carries a usable one (R6.3). */
function carriedPlanningYear(carried: URLSearchParams | undefined): number | undefined {
  const raw = carried?.get('planningYear');
  if (raw === undefined || raw === null) {
    return undefined;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isInteger(parsed) ? parsed : undefined;
}

/**
 * Apply a filter or sort change, returning to the first page.
 *
 * Narrowing the result set while holding a page offset is how a user lands on an
 * empty page that looks like "no matches": with 3 pages of results, page 3 held,
 * and a filter applied that leaves 4 rows, offset 40 is past the end. Every
 * state change except paging itself therefore resets the window.
 */
export function withFilterChange(
  state: InitiativeListState,
  change: Partial<InitiativeListState>,
): InitiativeListState {
  return { ...state, ...change, offset: 0 };
}

function isInitiativeStatus(value: string): value is InitiativeStatus {
  return initiativeStatusSchema.safeParse(value).success;
}

/**
 * The page size, restricted to the sizes the control offers.
 *
 * A URL asking for a size outside the offered set falls back to the default
 * rather than being honoured: the control could not represent it, so the
 * selector and the rendered page would disagree about the page size.
 */
function readPageSize(search: URLSearchParams): number {
  const parsed = Number.parseInt(search.get('limit') ?? '', 10);
  return LIST_PAGE_SIZES.includes(parsed as ListPageSize) ? parsed : DEFAULT_LIST_PAGE_SIZE;
}

/** The page offset: a non-negative integer within the API's own page bound. */
function readOffset(search: URLSearchParams): number {
  const parsed = Number.parseInt(search.get('offset') ?? '', 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  // The endpoint bounds `limit` at MAX_PAGE_SIZE; an offset beyond any plausible
  // population is clamped so a nonsense URL cannot ask for an absurd window.
  return Math.min(parsed, MAX_PAGE_SIZE * MAX_PAGE_SIZE);
}
